import { useQuery } from "@tanstack/react-query";
import axios from "./axios";
import toast from "react-hot-toast";
import dayjs from "dayjs";

// Types pour la nouvelle structure
type TVehiculeNFT = {
  id?: string;
  nftData?: {
    metadatas?: {
      vin?: string;
      ownerId?: string;
      linkedFiles?: any[];
      data?: string;
      number?: string;
      brand?: string;
      model?: string;
      pictureCid?: string | null;
      identificationId?: string;
      type?: string;
      antiTheftNumber?: string | null;
      registerStep?: string;
      gearBox?: string;
      power?: string;
      fuel?: string;
      dateOfFirstRegistration?: string;
      status?: string | null;
    };
    events?: TEvent[];
  };
  // Propriétés pour la compatibilité descendante
  brand?: string;
  model?: string;
  number?: string;
  gearBox?: string;
  dateOfFirstRegistration?: string;
  fuel?: string;
  power?: string;
  events?: TEvent[];
  eventsByDate?: { [date: string]: TEvent[] };
  metadata?: any;
};

type TEvent = {
  eventType: string;
  date: string;
  who: string;
  externalRef?: string;
  bcTxId?: string;
  data?: {
    vin?: string;
    ownerId?: string;
    linkedFiles?: any[];
    data?: string;
    number?: string;
    brand?: string;
    model?: string;
    pictureCid?: string | null;
    identificationId?: string;
    type?: string;
    antiTheftNumber?: string | null;
    registerStep?: string;
    gearBox?: string;
    power?: string;
    fuel?: string;
    dateOfFirstRegistration?: string;
    status?: string | null;
    name?: string;
    note?: string;
    url_report?: string[];
    eventType?: string;
    date?: string;
  };
};

export const vehiculesNFTKeys = {
  getVehiculeNFT: (immat: string) => ["vehicules", "immat", immat] as const,
};

export const useGetVehiculeNFT = (licensePlate: string) => {
  const { data, error, ...res } = useQuery({
    queryKey: vehiculesNFTKeys.getVehiculeNFT(licensePlate),
    queryFn: (): Promise<any> => axios.get(`/apy3nft/${licensePlate}`),
    enabled: licensePlate.trim().length > 0,
    staleTime: 0,
  });

  if (error) {
    const as = error as any;
    toast.error(as?.response?.data?.message || "Une erreur est survenue");
  }

  const dataTrue = data as any;
  console.log("Raw API response:", dataTrue);

  let vehicleData: TVehiculeNFT | undefined;
  let events: TEvent[] = [];
  const eventsByDate: { [date: string]: TEvent[] } = {};

  if (dataTrue?.data) {
    const responseData = dataTrue.data;
    console.log("Response data:", responseData);

    // Vérifier si on a la nouvelle structure avec nftData
    if (responseData.nftData) {
      console.log("Using new nftData structure");
      vehicleData = {
        id: responseData.id,
        nftData: responseData.nftData,
        // Remplir les propriétés de compatibilité descendante
        brand: responseData.nftData.metadatas?.brand,
        model: responseData.nftData.metadatas?.model,
        number: responseData.nftData.metadatas?.number,
        gearBox: responseData.nftData.metadatas?.gearBox,
        dateOfFirstRegistration: responseData.nftData.metadatas?.dateOfFirstRegistration,
        fuel: responseData.nftData.metadatas?.fuel,
        power: responseData.nftData.metadatas?.power,
      };

      // Traiter les événements de la nouvelle structure
      if (responseData.nftData.events && Array.isArray(responseData.nftData.events)) {
        events = responseData.nftData.events;
        console.log("Events from nftData:", events);
      }
    } 
    // Fallback vers l'ancienne structure
    else if (responseData.metadata) {
      console.log("Using old metadata structure");
      
      // Parser les métadonnées de l'ancienne structure
      if (responseData.metadata.metadata) {
        try {
          const parsedMetadata = typeof responseData.metadata.metadata === 'string' 
            ? JSON.parse(responseData.metadata.metadata)
            : responseData.metadata.metadata;
          
          vehicleData = {
            ...parsedMetadata,
            metadata: responseData.metadata
          };
          console.log("Parsed vehicle metadata:", vehicleData);
        } catch (error) {
          console.error("Error parsing metadata:", error);
          vehicleData = {
            metadata: responseData.metadata
          };
        }
      }

      // Parser les événements de l'ancienne structure
      if (responseData.metadata.events && Array.isArray(responseData.metadata.events)) {
        console.log("Processing old events structure");
        responseData.metadata.events.forEach((eventString: any, index: number) => {
          try {
            const key = Object.keys(eventString)[0];
            const eventValue = eventString[key];
            
            let eventData;
            if (typeof eventValue === 'string') {
              eventData = JSON.parse(eventValue);
            } else if (eventValue && typeof eventValue === 'object') {
              eventData = eventValue;
            }
            
            if (eventData && eventData.data) {
              events.push(eventData.data);
            } else if (eventData && eventData.eventType) {
              events.push(eventData);
            }
          } catch (error) {
            console.error(`Error parsing event at index ${index}:`, error);
          }
        });
      }
    }
    // Si aucune structure connue n'est trouvée, utiliser les données directement
    else {
      console.log("Using direct data structure");
      vehicleData = responseData;
    }

    // Trier les événements par date (plus récent en premier)
    if (events.length > 0) {
      events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      console.log("Sorted events:", events);

      // Grouper les événements par année
      events.forEach((event) => {
        try {
          const eventDate = dayjs(event.date).format("YYYY");
          if (!eventsByDate[eventDate]) {
            eventsByDate[eventDate] = [];
          }
          eventsByDate[eventDate].push(event);
        } catch (error) {
          console.error("Error grouping event by date:", error);
        }
      });
    }
  }

  // Préparer les données finales
  const dataToView: TVehiculeNFT = {
    ...vehicleData,
    events,
    eventsByDate,
  };

  console.log("Final vehicle data:", dataToView);

  return { vehicle: dataToView, ...res };
};